import React from "react";
import { ChevronUp, ChevronDown } from "../../../../components/icons/svg/outline";

/**
 * TicketLogHeader - Configurable table header with sortable columns
 * 
 * Renders table headers based on column configuration.
 * Supports sortable and non-sortable columns with responsive visibility.
 * 
 * @example
 * const columns = [
 *   { key: 'id', label: 'ID', sortable: true },
 *   { key: 'status', label: 'Status', sortable: false },
 *   { key: 'description', label: 'Description', sortable: true, hideOnMobile: true },
 * ];
 * 
 * @param {Object} props
 * @param {Array} props.columns - Column configuration array
 * @param {string} props.sortField - Currently sorted field
 * @param {string} props.sortDirection - Current sort direction ('asc' or 'desc')
 * @param {Function} props.onSort - Callback when column header is clicked
 */
const TicketLogHeader = ({ columns, sortField, sortDirection, onSort }) => {
  // Sort indicator icon component
  const SortIcon = ({ field }) => {
    const isActive = sortField === field;
    const iconClass = `w-4 h-4 inline ml-1 ${isActive ? "text-gray-900" : "text-gray-300"}`;

    return sortDirection === "asc" ? (
      <ChevronUp className={iconClass} />
    ) : (
      <ChevronDown className={iconClass} />
    );
  };

  // Sortable header cell component
  const SortableHeader = ({ column, children, index }) => {
    const style = {};
    if (column.width) style.width = column.width;
    if (column.maxWidth) style.maxWidth = column.maxWidth;
    if (column.minWidth) style.minWidth = column.minWidth;

    return (
      <th
        className={`${column.headerClassName || "px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"} ${column.hideOnMobile ? "hidden md:table-cell" : ""
          } ${column.maxWidth ? "truncate" : ""} ${index === 0 ? "pl-4" : ""} cursor-pointer hover:bg-gray-100 select-none`}
        style={Object.keys(style).length > 0 ? style : undefined}
        onClick={() => onSort(column.key)}
      >
        <div className="flex items-center">
          {children}
          <SortIcon field={column.key} />
        </div>
      </th>
    );
  };

  // Non-sortable header cell component
  const StaticHeader = ({ column, children }) => {
    const style = {};
    if (column.width) style.width = column.width;
    if (column.maxWidth) style.maxWidth = column.maxWidth;
    if (column.minWidth) style.minWidth = column.minWidth;

    return (
      <th
        className={`${column.headerClassName || "px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"} ${column.hideOnMobile ? "hidden md:table-cell" : ""
          } ${column.maxWidth ? "truncate" : ""}`}
        style={Object.keys(style).length > 0 ? style : undefined}
      >
        {children}
      </th>
    );
  };

  return (
    <thead className="bg-gray-50">
      <tr>
        {columns.map((column, index) => {
          const HeaderComponent = column.sortable ? SortableHeader : StaticHeader;

          return (
            <HeaderComponent key={column.key} column={column} index={index}>
              {column.label}
            </HeaderComponent>
          );
        })}
      </tr>
    </thead>
  );
};

export default TicketLogHeader;
